import { ScheduleTemplateResponse } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { ScheduleTermUnit } from "@common/types/domains/accounting/schedule";
import {
    ScheduleApplyPreviewInput,
    ScheduleApplyPreviewState,
    useScheduleApplyPreview,
} from "../useScheduleApplyPreview";
import { isAwaitingSpecificDate } from "./scheduleDraft";
import { ScheduleManualLayout } from "./scheduleManualLayout";
import {
    ScheduleDraftState,
    useScheduleDraftState,
} from "./useScheduleDraftState";

export interface ScheduleApplyDraft {
    draftState: ScheduleDraftState;
    preview: ScheduleApplyPreviewState;
    /** The start the schedule runs from: the term start, or the specific date when every row sits on it. */
    termStart: string;
    /** The draft resolves, passes its own checks and a preview of it has settled clean. */
    isApplicable: boolean;
    /** What the body shows under the rows per layout; the drawer's preview pane shows server errors itself. */
    draftErrorByLayout: Record<ScheduleManualLayout, string | null>;
}

/**
 * The editable draft, its ladder preview and the gate the apply modals share,
 * so the manual apply and the Specific date apply cannot drift apart.
 */
export function useScheduleApplyDraft(options: {
    template: ScheduleTemplateResponse | undefined;
    entityId: number | null;
    amount: number;
    termStart: string;
    /** The source's own day, when the schedule is applied to one. */
    sourceDate?: string | null;
    fixedAmount?: number;
    sourceRecognitionAccountId?: string | null;
    billing?: ScheduleApplyPreviewInput["billing"];
}): ScheduleApplyDraft {
    const {
        template,
        entityId,
        amount,
        termStart,
        sourceDate,
        fixedAmount,
        sourceRecognitionAccountId,
        billing,
    } = options;
    const draftState = useScheduleDraftState({
        template,
        entityId,
        start: {
            amount,
            termStart,
            specificDate: "",
            sourceDate,
            term: {
                length: template?.termLength ?? 0,
                unit: template?.termUnit ?? ScheduleTermUnit.months,
            },
        },
        fixedAmount,
        sourceRecognitionAccountId,
    });
    const { resolved } = draftState;
    const previewTermStart =
        resolved?.termStart ?? resolved?.specificDate ?? "";
    const preview = useScheduleApplyPreview({
        template,
        amount: resolved?.amount ?? 0,
        termStart: isAwaitingSpecificDate(draftState.draft)
            ? ""
            : previewTermStart,
        sourceDate,
        specificDate: resolved?.specificDate ?? "",
        term: draftState.draft
            ? {
                  length: draftState.draft.termLength,
                  unit: draftState.draft.termUnit,
              }
            : undefined,
        components: resolved?.components,
        billing,
    });
    return {
        draftState,
        preview,
        termStart: previewTermStart,
        isApplicable:
            resolved !== null &&
            draftState.error === null &&
            preview.isRequested &&
            preview.errorMessage === null &&
            preview.isSettled,
        draftErrorByLayout: {
            modal: draftState.error ?? preview.errorMessage,
            drawer: draftState.error,
        },
    };
}
